import React, { useState } from 'react';
import ReportLayout from '../../components/Layout/ReportLayout';
import styles from '../../assets/styles/report/ReportOutput.module.scss';
import { motion, Reorder, AnimatePresence } from 'framer-motion';

// Mock Data for Slides
const INITIAL_ANALYSIS_SLIDES = [
  { id: 'a1', title: '表紙', type: 'cover', checked: true, description: 'レポートの表紙です。対象期間とクライアント名を表示します。' },
  { id: 'a2', title: 'サイト月次推移サマリ', type: 'chart', checked: true, description: 'サイト全体のセッション、CV、CVRの月次推移グラフです。' },
  { id: 'a3', title: '参照元別成果', type: 'table', checked: true, description: '流入チャネルごとの獲得成果一覧表です。' },
  { id: 'a4', title: 'サイトページ分析', type: 'ga4', checked: true, description: '主要ページの閲覧数とCV貢献度の分析です。' },
  { id: 'a5', title: '広告月次推移サマリ', type: 'chart', checked: true, description: '広告全体のコスト、クリック、CVの月次推移グラフです。' },
  { id: 'a6', title: '検索広告実績', type: 'ad', checked: true, description: 'リスティング広告のキャンペーン別成果です。' },
  { id: 'a7', title: 'エリア別実績', type: 'map', checked: false, description: '都道府県別の配信実績マップとリストです。' },
  { id: 'a8', title: 'ディスプレイ広告実績', type: 'ad', checked: false, description: 'GDN/YDA等のバナー広告成果です。' },
  { id: 'a9', title: '広告文分析', type: 'table', checked: false, description: '検索広告の広告文ごとの成果一覧表です。' },
  { id: 'a10', title: 'P-MAX/Youtube配信状況', type: 'chart', checked: false, description: 'P-MAXの配分とYoutube動画の成果分析です。' },
];

const INITIAL_PROPOSAL_SLIDES = [
  { id: 'p0', title: '表紙', type: 'cover', checked: true, description: 'レポートの表紙です。対象期間とクライアント名を表示します。' },
  { id: 'p1', title: '市場トレンド分析', type: 'market', checked: true, description: '業界全体の検索トレンドと需要予測です。' },
  { id: 'p2', title: '競合比較分析', type: 'competitor', checked: true, description: '主要競合他社との広告出稿量比較です。' },
  { id: 'p3', title: 'ターゲットインサイト', type: 'user', checked: true, description: 'ユーザーの興味関心・属性データの分析です。' },
  { id: 'p4', title: '改善施策のご提案', type: 'proposal', checked: true, description: '来月の具体的な運用改善案です。' },
  { id: 'p5', title: 'KPIシミュレーション', type: 'kpi', checked: true, description: '次月の予測数値と目標達成ラインの提示です。' },
  { id: 'p6', title: '実施スケジュール', type: 'schedule', checked: true, description: '施策の実行スケジュールと担当区分です。' },
];

// Reusable Slide List Component
const SlideList = ({ title, slides, setSlides, onToggle, color }: {
  title: string,
  slides: typeof INITIAL_ANALYSIS_SLIDES,
  setSlides: React.Dispatch<React.SetStateAction<typeof INITIAL_ANALYSIS_SLIDES>>,
  onToggle: (id: string) => void,
  color: string
}) => (
  <div className={styles.slideSection}>
    {title && (
      <h3 className={styles.slideTitle}>
        <span className={styles.colorBar} style={{ background: color }}></span>
        {title}
      </h3>
    )}
    <Reorder.Group axis="y" values={slides} onReorder={setSlides} className={styles.slideGrid}>
      {slides.map((slide) => (
        <Reorder.Item
          key={slide.id}
          value={slide}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className={styles.slideCard}
          style={{
            background: slide.checked ? `${color}10` : 'rgba(255,255,255,0.02)', // Hex alpha
            borderColor: slide.checked ? color : 'rgba(255,255,255,0.1)',
          }}
        >
          <div
            className={styles.slideClickArea}
            onClick={(e) => {
              e.stopPropagation();
              onToggle(slide.id);
            }}
          >
            <div
              className={styles.slideCheckbox}
              style={{
                border: slide.checked ? 'none' : '2px solid #555',
                background: slide.checked ? color : 'transparent',
              }}
            >
              {slide.checked && '✓'}
            </div>
            <div className={styles.slideContent}>
              <span className={`${styles.stitle} ${slide.checked ? styles.checked : ''}`}>{slide.title}</span>
              <span className={styles.sdesc}>{slide.description}</span>
            </div>
          </div>
          <div className={styles.slideDragHandle}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6"></line><line x1="8" y1="12" x2="21" y2="12"></line><line x1="8" y1="18" x2="21" y2="18"></line><line x1="3" y1="6" x2="3.01" y2="6"></line><line x1="3" y1="12" x2="3.01" y2="12"></line><line x1="3" y1="18" x2="3.01" y2="18"></line></svg>
          </div>
        </Reorder.Item>
      ))}
    </Reorder.Group>
  </div>
);

const ReportOutput: React.FC = () => {
  const [analysisSlides, setAnalysisSlides] = useState(INITIAL_ANALYSIS_SLIDES);
  const [proposalSlides, setProposalSlides] = useState(INITIAL_PROPOSAL_SLIDES);

  // Settings State
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [settings, setSettings] = useState({
    paperSize: 'A4',
    companyName: 'Aibex',
    clientName: '株式会社Inovis'
  });

  // Colors
  const ANALYSIS_COLOR = '#00f2ff';
  const PROPOSAL_COLOR = '#d455d4'; // Toned down pink/purple

  const toggleAnalysis = (id: string) => {
    setAnalysisSlides(prev => prev.map(s => s.id === id ? { ...s, checked: !s.checked } : s));
  };

  const toggleProposal = (id: string) => {
    setProposalSlides(prev => prev.map(s => s.id === id ? { ...s, checked: !s.checked } : s));
  };

  const handleGenerate = (type: 'analysis' | 'proposal') => {
    const slides = type === 'analysis' ? analysisSlides : proposalSlides;
    const selectedIds = slides.filter(s => s.checked).map(s => s.id).join(',');

    // Encode settings to URL params
    const params = new URLSearchParams({
      type,
      ids: selectedIds,
      paper: settings.paperSize,
      company: settings.companyName,
      client: settings.clientName
    });

    window.open(`/report/preview?${params.toString()}`, '_blank');
  };

  return (
    <ReportLayout>
      <div className={styles.reportContent}>
        <div className={styles.container}>

          {/* Header with Buttons */}
          <div className={styles.pageHeader}>
            <div className={styles.headerMeta}>
              <h2>レポート出力設定</h2>
              <p>出力するスライドを選択し、各レポートの「生成」ボタンを押してください。</p>
            </div>
            <div className={styles.headerActions}>
              <button
                onClick={() => setIsSettingsOpen(true)}
                className={styles.btnSettings}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.18-.08a2 2 0 0 0-2 2v.44a2 2 0 0 0 2 2h.18a2 2 0 0 1 1.73 1l.25.43a2 2 0 0 1 0 2l-.08.18a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.18.08a2 2 0 0 0 2-2v-.44a2 2 0 0 0-2-2h-.18a2 2 0 0 1-1.73-1l-.25-.43a2 2 0 0 1 0-2l.08-.18a2 2 0 0 0-2-2z"></path><circle cx="12" cy="12" r="3"></circle></svg>
                レポート設定
              </button>
              <button
                className={styles.btnSave}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path><polyline points="17 21 17 13 7 13 7 21"></polyline><polyline points="7 3 7 8 15 8"></polyline></svg>
                出力設定を保存
              </button>
            </div>
          </div>

          <div className={styles.mainGrid}>

            {/* Analysis Report Section */}
            <div className={styles.colSection}>
              <div className={styles.sectionHeader}>
                <h3 className={styles.sectionTitle}>
                  <span className={styles.colorBar} style={{ background: ANALYSIS_COLOR }}></span>
                  分析レポート（定期報告）
                </h3>
                <button
                  onClick={() => handleGenerate('analysis')}
                  className={`${styles.btnGenerate} ${styles.analysis}`}
                  style={{
                    background: `linear-gradient(45deg, ${ANALYSIS_COLOR}, #00c6ff)`,
                    boxShadow: `0 2px 8px ${ANALYSIS_COLOR}50`,
                  }}
                >
                  レポート生成
                </button>
              </div>
              <SlideList
                title=""
                slides={analysisSlides}
                setSlides={setAnalysisSlides}
                onToggle={toggleAnalysis}
                color={ANALYSIS_COLOR}
              />
            </div>

            {/* Divider 1 */}
            <div className={styles.verticalDivider}></div>

            {/* Proposal Report Section */}
            <div className={styles.colSection}>
              <div className={styles.sectionHeader}>
                <h3 className={styles.sectionTitle}>
                  <span className={styles.colorBar} style={{ background: PROPOSAL_COLOR }}></span>
                  提案レポート（改善提案）
                </h3>
                <button
                  onClick={() => handleGenerate('proposal')}
                  className={styles.btnGenerate}
                  style={{
                    background: `linear-gradient(45deg, ${PROPOSAL_COLOR}, #ff0088)`,
                    boxShadow: `0 2px 8px ${PROPOSAL_COLOR}50`,
                  }}
                >
                  レポート生成
                </button>
              </div>
              <SlideList
                title=""
                slides={proposalSlides}
                setSlides={setProposalSlides}
                onToggle={toggleProposal}
                color={PROPOSAL_COLOR}
              />
            </div>

            {/* Divider 2 */}
            <div className={styles.verticalDivider}></div>

            {/* Past Reports (History) */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <h3 className={styles.historyTitle}>過去の生成レポート</h3>
              <div className={styles.historyList}>
                {[
                  { date: '2025.11.30', type: '分析', color: ANALYSIS_COLOR },
                  { date: '2025.10.31', type: '分析', color: ANALYSIS_COLOR },
                  { date: '2025.10.31', type: '提案', color: PROPOSAL_COLOR },
                  { date: '2025.09.30', type: '分析', color: ANALYSIS_COLOR },
                ].map((item, i) => (
                  <div key={i} className={styles.historyItem}>
                    <div
                      className={styles.historyIcon}
                      style={{
                        background: `${item.color}20`,
                        color: item.color,
                      }}
                    >PDF</div>
                    <div className={styles.historyMeta}>
                      <div className={styles.hdate}>{item.date} {item.type}</div>
                      <div className={styles.hsize}>1.2MB</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>

        {/* Settings Modal */}
        <AnimatePresence>
          {isSettingsOpen && (
            <div className={styles.modalOverlay}>
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className={styles.modalContent}
              >
                <h3 className={styles.modalTitle}>レポート初期設定</h3>

                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>用紙サイズ</label>
                  <select
                    value={settings.paperSize}
                    onChange={(e) => setSettings({ ...settings, paperSize: e.target.value })}
                    className={styles.formSelect}
                  >
                    <option value="A4">A4 (横)</option>
                  </select>
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>自社名 (表紙右下に表示)</label>
                  <input
                    type="text"
                    value={settings.companyName}
                    onChange={(e) => setSettings({ ...settings, companyName: e.target.value })}
                    className={styles.formInput}
                  />
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>送付先顧客名 (表紙左上に表示)</label>
                  <input
                    type="text"
                    value={settings.clientName}
                    onChange={(e) => setSettings({ ...settings, clientName: e.target.value })}
                    className={styles.formInput}
                  />
                </div>

                <div className={styles.modalActions}>
                  <button
                    onClick={() => setIsSettingsOpen(false)}
                    className={styles.btnCancel}
                  >
                    キャンセル
                  </button>
                  <button
                    onClick={() => setIsSettingsOpen(false)}
                    className={styles.btnSubmit}
                  >
                    設定を保存
                  </button>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>
      </div>
    </ReportLayout>
  );
};

export default ReportOutput;
